import React, { createContext, useContext, useState } from 'react';

export enum AudioPlayerState {
  Play = 'play',
  Pause = 'pause',
  Stop = 'stop',
}

export interface AudioItem {
  src: string;
  metadata?: any;
  audio: HTMLAudioElement;
  managerStop: (newItem?: AudioItem) => void;
}

interface AudioItemState {
  item: AudioItem;
  timeElapsedMs: number;
  timeLeftMs: number;
}

interface AudioManagerConfig {
  preloadCount: number;
  preloadTimeThresholdMs: number;
}

export class AudioManager {
  private items: AudioItemState[] = [];
  private currentIndex: number = null;
  private currentState: AudioPlayerState = AudioPlayerState.Stop;

  constructor(
    public readonly config: AudioManagerConfig = {
      preloadCount: 2,
      preloadTimeThresholdMs: 10000,
    },
  ) {}

  register(item: AudioItem) {
    const itemState = this.getItemState(item.src);
    if (itemState) {
      itemState.item = item;
      return;
    }

    this.items.push({ item, timeElapsedMs: 0, timeLeftMs: 0 });
    return this.items.length - 1;
  }

  onPlay(src: string) {
    this.currentState = AudioPlayerState.Play;

    const index = this.getIndex(src);
    const newItemState = this.getItemState(src);
    if (index !== this.currentIndex) {
      this.items[this.currentIndex]?.item.managerStop(newItemState?.item);
    }

    this.currentIndex = index;
  }

  onProgress(src: string, timeElapsedMs: number, timeLeftMs: number) {
    const item = this.getItemState(src);
    item.timeElapsedMs = timeElapsedMs;
    item.timeLeftMs = timeLeftMs;
  }

  onPause() {
    this.currentState = AudioPlayerState.Pause;
  }

  onStop() {
    this.currentState = AudioPlayerState.Stop;
    this.currentIndex = null;
  }

  private getItemState(src: string): AudioItemState | null {
    const index = this.getIndex(src);
    if (index === null) {
      return null;
    }

    return this.items[index];
  }

  private getIndex(src: string): number | null {
    const index = this.items.findIndex((item) => item.item.src === src);
    if (index >= 0) {
      return index;
    }

    return null;
  }
}

interface AudioContextType {
  audioManager: AudioManager;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [audioManager] = useState(new AudioManager());

  return (
    <AudioContext.Provider value={{ audioManager }}>
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};
